import { ChangeEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

export const toSortedKey = (arr: readonly string[]) => [...arr].sort().join('|');

export const setAll = (sp: URLSearchParams, key: string, values?: readonly string[]) => {
  sp.delete(key);
  values?.forEach((v) => sp.append(key, v));
};

type PatchValue = string | readonly string[] | undefined;

export function useUrlSyncedState<const K extends readonly string[]>(keys: K) {
  type Key = K[number];
  type State = Record<Key, string[]>;

  const [searchParams, setSearchParams] = useSearchParams();
  const keysTyped = useMemo(() => keys as readonly Key[], [keys]);

  const hasPage = useMemo(() => keysTyped.includes('page' as Key), [keysTyped]);
  const hasSearch = useMemo(() => keysTyped.includes('search' as Key), [keysTyped]);
  const hasSortBy = useMemo(() => keysTyped.includes('sortBy' as Key), [keysTyped]);
  const hasSortDirection = useMemo(() => keysTyped.includes('sortDirection' as Key), [keysTyped]);

  const parse = useCallback(
    (sp: URLSearchParams) => Object.fromEntries(keysTyped.map((k) => [k, sp.getAll(k)])) as State,
    [keysTyped],
  );

  const equals = useCallback(
    (a: State, b: State) => keysTyped.every((k) => toSortedKey(a[k]) === toSortedKey(b[k])),
    [keysTyped],
  );

  const [state, setState] = useState<State>(() => parse(searchParams));
  const skipNextWriteRef = useRef(false);

  const setMany = useCallback(
    (patch: Partial<Record<Key, PatchValue>>, resetPage = false) => {
      setState((prev) => {
        const next = { ...prev };

        (Object.entries(patch) as Array<[Key, PatchValue]>).forEach(([k, v]) => {
          if (v === undefined) {
            next[k] = [];
            return;
          }
          if (typeof v === 'string') {
            next[k] = v ? [v] : [];
            return;
          }
          next[k] = [...v].filter(Boolean);
        });

        if (resetPage && hasPage) {
          next['page' as Key] = [];
        }

        return equals(prev, next) ? prev : next;
      });
    },
    [equals, hasPage],
  );

  const page = useMemo(() => {
    if (!hasPage) return 1;
    const raw = state['page' as Key]?.[0];
    const n = raw ? Number(raw) : 1;
    return Number.isFinite(n) && n > 0 ? n : 1;
  }, [hasPage, state]);

  const setPage = useCallback(
    (nextPage: number) => {
      if (!hasPage) return;
      const safe = Number.isFinite(nextPage) && nextPage > 0 ? nextPage : 1;

      if (safe === 1) {
        setMany({ ['page' as Key]: undefined } as Partial<Record<Key, PatchValue>>);
      } else {
        setMany({ ['page' as Key]: String(safe) } as Partial<Record<Key, PatchValue>>);
      }
    },
    [hasPage, setMany],
  );

  const setPageValue = useCallback(
    (_: ChangeEvent<unknown>, value: number) => setPage(value),
    [setPage],
  );

  const searchValue = useMemo(() => {
    if (!hasSearch) return '';
    return state['search' as Key]?.[0] ?? '';
  }, [hasSearch, state]);

  const setSearchValue = useCallback(
    (val: string) => {
      if (!hasSearch) return;
      const v = val ?? '';
      setMany(
        { ['search' as Key]: v.trim() ? v : undefined } as Partial<Record<Key, PatchValue>>,
        true,
      );
    },
    [hasSearch, setMany],
  );

  const sortBy = useMemo(() => {
    if (!hasSortBy) return '';
    return state['sortBy' as Key]?.[0] ?? '';
  }, [hasSortBy, state]);

  const setSortByValue = useCallback(
    (val: string, resetPage = true) => {
      if (!hasSortBy) return;
      setMany({ ['sortBy' as Key]: val } as Partial<Record<Key, PatchValue>>, resetPage);
    },
    [hasSortBy, setMany],
  );

  const sortDirection = useMemo(() => {
    if (!hasSortDirection) return '';
    return state['sortDirection' as Key]?.[0] ?? '';
  }, [hasSortDirection, state]);

  const setSortDirectionValue = useCallback(
    (val: 'asc' | 'desc', resetPage = true) => {
      if (!hasSortDirection) return;
      setMany({ ['sortDirection' as Key]: val } as Partial<Record<Key, PatchValue>>, resetPage);
    },
    [hasSortDirection, setMany],
  );

  useEffect(() => {
    const next = parse(searchParams);

    setState((prev) => {
      if (equals(prev, next)) return prev;
      skipNextWriteRef.current = true;
      return next;
    });
  }, [searchParams, parse, equals]);

  useEffect(() => {
    if (skipNextWriteRef.current) {
      skipNextWriteRef.current = false;
      return;
    }

    setSearchParams(
      (prev) => {
        const prevStr = prev.toString();
        const next = new URLSearchParams(prev);

        keysTyped.forEach((k) => setAll(next, k, state[k]));

        return next.toString() === prevStr ? prev : next;
      },
      { replace: true },
    );
  }, [state, keysTyped, setSearchParams]);

  return {
    state,
    setState,
    setMany,
    page,
    setPage,
    setPageValue,
    searchValue,
    setSearchValue,
    sortBy,
    setSortByValue,
    sortDirection,
    setSortDirectionValue,
    searchParams,
  };
}
